import React, { useEffect, useState, useMemo, useCallback } from "react";
import { 
    collection, getDocs, doc, updateDoc, serverTimestamp, getDoc 
} from "firebase/firestore";
import { FIRESTORE_DB } from "../../Database/Database"; 
import { NavBarDashboard } from "../../Components/NavBarDashboard/NavBarDashboard";
import { FooterDashboard } from "../../Components/FooterDashboard/FooterDashboard";

import { 
    Container, Row, Col, Card, Button, Spinner, Form, Alert, Badge, Modal, 
    Table, ListGroup
} from "react-bootstrap";
// Reutiliza tus componentes (Asegúrate de que las rutas sean correctas)
import { BuscadorProductos } from "./components/BuscadorProductos"; 
import { ListaProductos } from "./components/ListaProductos";
import { Carrito } from "./components/Carrito"; 

import { generarIDTicket } from "./helpers/generarIDTicket";
import { registrarVenta } from "./api/ventas";


// ⚠️ IMPORTANTE: Ajusta esta constante al nombre exacto en tu DB.
const VENDEDOR_NOMBRE_TIENDA_NUBE = "tiendanube"; 

// ======================================================================
// LÓGICA DE FECHA (7 DÍAS HÁBILES)
// ======================================================================
/**
 * Calcula una fecha 7 días hábiles (Lunes a Viernes) después de la fecha de inicio.
 * @param {Date | object} startDate La fecha de inicio (puede ser un objeto de Firebase Timestamp o un objeto Date).
 * @returns {Date} La fecha de cobro estimada.
 */
const calcularFechaCobro = (startDate) => {
    const start = startDate instanceof Date 
        ? startDate 
        : (startDate && startDate.toDate ? startDate.toDate() : new Date());

    let daysToAdd = 7;
    let currentDate = new Date(start);
    
    while (daysToAdd > 0) {
        currentDate.setDate(currentDate.getDate() + 1); 
        const dayOfWeek = currentDate.getDay(); // 0 = Domingo, 6 = Sábado

        if (dayOfWeek !== 0 && dayOfWeek !== 6) {
            daysToAdd--;
        }
    }
    return currentDate;
};

const CargaTiendaNube = () => {
    const [productos, setProductos] = useState([]);
    const [filtros, setFiltros] = useState({ codigo: "", categoria: "", proveedor: "", nombre: "" });
    const [carrito, setCarrito] = useState([]);
    const [loading, setLoading] = useState(false);
    
    // 🚀 NUEVO ESTADO: Referencia de Tienda Nube
    const [referenciaTiendaNube, setReferenciaTiendaNube] = useState("");
    
    // ESTADOS PARA VENTAS REGISTRADAS
    const [loadingVentas, setLoadingVentas] = useState(false); 
    const [ventasTiendaNube, setVentasTiendaNube] = useState([]); 
    
    // ESTADO PARA EL FILTRO DE ESTADO DE VENTA
    const [filtroEstadoVenta, setFiltroEstadoVenta] = useState("Todas"); // 'Todas', 'Cobrado', 'PendienteCaja', 'Anulada'

    // ESTADOS DE MODAL
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [selectedVenta, setSelectedVenta] = useState(null);

    const [vendedorTiendaNube, setVendedorTiendaNube] = useState(null);
    const [flashMessage, setFlashMessage] = useState(null);

    const showFlashMessage = useCallback((message, variant = 'info') => {
        setFlashMessage({ message, variant });
        const timeout = setTimeout(() => {
            setFlashMessage(null);
        }, 5000); 
        return () => clearTimeout(timeout);
    }, []);

    // ----------------------------------------------------------------------
    // Carga de Datos
    // ----------------------------------------------------------------------

    const loadData = async () => {
        setLoading(true);
        setLoadingVentas(true); 
        
        try {
            // Cargar Productos
            const snapProductos = await getDocs(collection(FIRESTORE_DB, "productos"));
            const listProductos = snapProductos.docs.map((d) => ({ id: d.id, ...d.data() }));
            setProductos(listProductos);

            // Buscar Vendedor Tienda Nube (Mantenido)
            const snapVendedores = await getDocs(collection(FIRESTORE_DB, "vendedores"));
            const vendedorTN = snapVendedores.docs.map((d) => ({ id: d.id, ...d.data() }))
                                                 .find(v => v.nombre === VENDEDOR_NOMBRE_TIENDA_NUBE);
            
            if (vendedorTN) {
                setVendedorTiendaNube(vendedorTN);
            } else {
                showFlashMessage(`Vendedor "${VENDEDOR_NOMBRE_TIENDA_NUBE}" no encontrado...`, 'danger');
            }
            
            // Cargar TODAS las Ventas de Tienda Nube
            const snapVentas = await getDocs(collection(FIRESTORE_DB, "ventas"));
            const listVentas = snapVentas.docs.map((d) => ({ 
                id: d.id, 
                ...d.data(), 
                fechaCobroEstimada: calcularFechaCobro(d.data().fecha) 
            })).filter(v => v.origenVenta === "TiendaNube");

            // Ordenar por fecha de más reciente a más antigua
            listVentas.sort((a, b) => b.fecha.seconds - a.fecha.seconds);
            setVentasTiendaNube(listVentas);


        } catch (error) {
            showFlashMessage("Error al cargar datos iniciales. Verifique su conexión.", 'danger');
            console.error(error);
        } finally {
            setLoading(false);
            setLoadingVentas(false);
        }
    };

    useEffect(() => {
        loadData();
    }, []);

    // ----------------------------------------------------------------------
    // LÓGICA DE FILTROS, CARrito y Helpers
    // ----------------------------------------------------------------------
    
    const categorias = useMemo(() => {
        const s = new Set();
        productos.forEach(p => p.categoria && s.add(p.categoria));
        return Array.from(s);
    }, [productos]);

    const proveedores = useMemo(() => {
        const s = new Set();
        productos.forEach(p => p.proveedor && s.add(p.proveedor));
        return Array.from(s);
    }, [productos]);

    const productosFiltrados = useMemo(() => {
        return productos.filter(p => {
            if (filtros.codigo && !(String(p.codigoProducto || "").toLowerCase().includes(filtros.codigo.toLowerCase()))) return false;
            if (filtros.categoria && p.categoria !== filtros.categoria) return false;
            if (filtros.proveedor && p.proveedor !== filtros.proveedor) return false;
            if (filtros.nombre && !(String(p.nombreProducto || "").toLowerCase().includes(filtros.nombre.toLowerCase()))) return false;
            return true;
        });
    }, [productos, filtros]);
    
    // LÓGICA DE FILTRADO DE VENTAS POR ESTADO
    const ventasFiltradasPorEstado = useMemo(() => {
        if (filtroEstadoVenta === "Todas") {
            return ventasTiendaNube;
        } else if (filtroEstadoVenta === "Anulada") {
            // Filtra por estado: 'anulada'
            return ventasTiendaNube.filter(v => v.estado === 'anulada');
        } else {
            // Filtra por estadoPago: 'Cobrado' o 'PendienteCaja' (y NO anuladas)
            return ventasTiendaNube.filter(v => 
                v.estadoPago === filtroEstadoVenta && v.estado !== 'anulada'
            );
        }
    }, [ventasTiendaNube, filtroEstadoVenta]);

    // Permite stock negativo/cero para ventas online ya realizadas
    const agregarAlCarrito = (prod) => {
        const exists = carrito.find(p => p.id === prod.id);
        if (exists) {
            setCarrito(carrito.map(p => p.id === prod.id ? { ...p, cantidad: p.cantidad + 1 } : p));
        } else {
            setCarrito([...carrito, { ...prod, cantidad: 1 }]);
        }
    };

    const cambiarCantidad = (id, cantidad) => {
        if (cantidad <= 0) return;
        setCarrito(carrito.map(p => p.id === id ? { ...p, cantidad } : p));
    };

    const quitarDelCarrito = (id) => {
        setCarrito(carrito.filter(p => p.id !== id));
    };

    const limpiarFiltros = () => setFiltros({ codigo: "", categoria: "", proveedor: "", nombre: "" });
    const limpiarCarrito = () => setCarrito([]);

    const totalSinDescuento = useMemo(() => {
        return carrito.reduce(
            (s, it) => s + (Number(it.precioFinal || 0) * it.cantidad),
            0
        );
    }, [carrito]);
    
    const totalFinal = totalSinDescuento; 


    // ----------------------------------------------------------------------
    // Lógica del Modal de Detalle
    // ----------------------------------------------------------------------
    const handleShowDetails = (venta) => {
        setSelectedVenta(venta);
        setShowDetailModal(true);
    };

    const handleCloseDetails = () => {
        setShowDetailModal(false);
        setSelectedVenta(null);
    };
    
    // ----------------------------------------------------------------------
    // Lógica de Finalización de Venta
    // ----------------------------------------------------------------------

    const handleFinalizarTN = async () => {
        if (carrito.length === 0) return showFlashMessage("El carrito está vacío.", 'danger');
        if (!vendedorTiendaNube) return showFlashMessage("Error: Vendedor Tiendanube no configurado.", 'danger');

        // 🚀 VALIDACIÓN DE REFERENCIA (NUEVO)
        if (!referenciaTiendaNube.trim()) {
            return showFlashMessage("Debe ingresar la Referencia de Pedido de Tienda Nube (Ej: 102).", 'danger');
        }

        setLoading(true);
        const ticketId = generarIDTicket(); 
        const estadoPago = "PendienteCaja"; 

        const ventaPayload = {
            ticketId,
            
            // 🚀 GUARDAR REFERENCIA EXTERNA (NUEVO CAMPO)
            referenciaTiendaNube: referenciaTiendaNube.trim(),
            
            productos: carrito.map(it => ({
                idProducto: it.id,
                codigoProducto: it.codigoProducto,
                nombre: it.nombreProducto,
                categoria: it.categoria,
                cantidad: it.cantidad,
                precioUnitario: Number(it.precioFinal || 0),
                subtotal: Number(it.precioFinal || 0) * it.cantidad,
                precioCosto: Number(it.precioCosto || 0),
                stockActual: Number(it.stock || 0)
            })),
            totalSinDescuento: totalSinDescuento,
            descuentoAplicado: 0, 
            promoDescuento: 0,
            motivoPromo: "Venta Tienda Nube",
            totalFinal: totalFinal, 
            
            metodoPago: "TiendaNube", 
            
            cliente: "Tienda Nube (Online)",
            estado: "completada",
            vendedor: {
                id: vendedorTiendaNube.id,
                nombre: vendedorTiendaNube.nombre
            },
            
            origenVenta: "TiendaNube",
            estadoPago: estadoPago,
            fecha: serverTimestamp(), 
        };

        try {
            const res = await registrarVenta(ventaPayload);
            if (!res || !res.success) throw new Error("Error desconocido al registrar venta");
            
            // Actualización de Stock
            for (const item of carrito) {
                const productoRef = doc(FIRESTORE_DB, "productos", item.id);
                const nuevoStock = Number(item.stock || 0) - item.cantidad; 
                await updateDoc(productoRef, { stock: nuevoStock });
            }

            showFlashMessage(`Venta de Tienda Nube registrada (ID: ${res.id}). Stock actualizado. Estado: ${estadoPago}.`, 'success');
            limpiarCarrito();
            
            // 🚀 LIMPIAR EL NUEVO ESTADO DE REFERENCIA
            setReferenciaTiendaNube(""); 
            
            await loadData(); 
            
        } catch (err) {
            console.error(err);
            showFlashMessage("Error al finalizar la venta de Tienda Nube.", 'danger');
        }

        setLoading(false);
    };
    
    // ----------------------------------------------------------------------
    // Lógica para Cambiar Estado de Cobro (Mantenido)
    // ----------------------------------------------------------------------
    const handleToggleCobradoStatus = async (venta) => {
        if (venta.estado === 'anulada') {
            showFlashMessage("No se puede modificar el estado de pago de una venta anulada.", 'warning');
            return;
        }

        setLoading(true);
        
        const newStatus = venta.estadoPago === 'Cobrado' ? 'PendienteCaja' : 'Cobrado';
        const ventaRef = doc(FIRESTORE_DB, "ventas", venta.id);
        
        try {
            await updateDoc(ventaRef, { 
                estadoPago: newStatus 
            });

            // Actualizar el estado local para que se refleje inmediatamente en el modal
            setSelectedVenta(prev => ({ 
                ...prev, 
                estadoPago: newStatus 
            }));
            
            showFlashMessage(`Estado de pago de Venta ${venta.ticketId} actualizado a ${newStatus}.`, 'success');
            
            // Recargar datos para actualizar la lista principal
            await loadData(); 

        } catch (err) {
            console.error(err);
            showFlashMessage("Error al actualizar el estado de cobro.", 'danger');
        } finally {
            setLoading(false);
        }
    };


    // ----------------------------------------------------------------------
    // Lógica de Anulación de Venta (Mantenido)
    // ----------------------------------------------------------------------
    const handleAnularVenta = async (venta) => {
        if (venta.estado === 'anulada') {
            showFlashMessage("La venta ya está anulada.", 'warning');
            return;
        }

        if (!window.confirm(`¿Está seguro de ANULAR la venta ${venta.ticketId} (Ref. ${venta.referenciaTiendaNube || 'N/A'}, Total: $${venta.totalFinal})? ESTO REVERTIRÁ EL STOCK DESCONTADO.`)) {
            return;
        }
        setLoading(true);
        
        try {
            // 1. Revertir Stock
            for (const item of venta.productos) {
                const productoRef = doc(FIRESTORE_DB, "productos", item.idProducto);
                
                // Buscar stock actual en el estado 'productos'
                const currentProduct = productos.find(p => p.id === item.idProducto);
                
                const currentStock = Number(currentProduct ? currentProduct.stock : 0);
                const nuevoStock = currentStock + item.cantidad; // Revertir el stock
                
                await updateDoc(productoRef, { stock: nuevoStock });
            }
            
            // 2. Actualizar estado de la venta
            const ventaRef = doc(FIRESTORE_DB, "ventas", venta.id);
            await updateDoc(ventaRef, { 
                estado: "anulada", 
                estadoPago: "Anulado",
                fechaAnulacion: serverTimestamp() 
            });

            showFlashMessage(`Venta ${venta.ticketId} (Ref. ${venta.referenciaTiendaNube || 'N/A'}) anulada y stock revertido correctamente.`, 'success');
            handleCloseDetails();
            await loadData(); 
            
        } catch (err) {
            console.error(err);
            showFlashMessage("Error al anular la venta. Revise la consola.", 'danger');
        }

        setLoading(false);
    };


    return (
        <>
            <NavBarDashboard />
            <Container fluid className="py-4" style={{ background: "#f5f7fb", minHeight: "100vh" }}>
                
                {/* ALERTA DE MENSAJE FLASH */}
                {flashMessage && (
                    <Alert variant={flashMessage.variant} onClose={() => setFlashMessage(null)} dismissible className="mb-3">
                        {flashMessage.message}
                    </Alert>
                )}
                
                <Row className="mb-3">
                    <Col>
                        <h3>
                            Carga de Ventas Tienda Nube 
                            <Badge bg="warning" className="ms-3 align-text-bottom">
                                PAGO PENDIENTE (Se cambia al ver el detalle)
                            </Badge>
                        </h3>
                        <p className="text-muted">Usar para ingresar ventas ya realizadas en tu plataforma online y descontar stock.</p>
                        {vendedorTiendaNube ? (
                             <Alert variant="info" className="p-2">
                                Vendedor activo para esta carga: **{VENDEDOR_NOMBRE_TIENDA_NUBE}** </Alert>
                        ) : (
                             <Alert variant="danger" className="p-2">
                                ⚠️ Vendedor "**{VENDEDOR_NOMBRE_TIENDA_NUBE}**" no encontrado. Venta no se registrará correctamente.
                            </Alert>
                        )}
                    </Col>
                </Row>

                <Row className="mb-3">
                    <Col>
                        <Card className="shadow-sm border-0 p-3">
                            <BuscadorProductos
                                filtros={filtros}
                                setFiltros={setFiltros}
                                categorias={categorias} 
                                proveedores={proveedores} 
                                onClear={limpiarFiltros} 
                            />
                        </Card>
                    </Col>
                </Row>

                {/* SECCIÓN PRINCIPAL DE CARGA */}
                <Row className="g-4">
                    <Col xs={12} md={7}>
                        <Card className="shadow-sm border-0">
                            <Card.Body>
                                <Card.Title>Productos disponibles</Card.Title>
                                <hr />
                                <ListaProductos 
                                    productos={productosFiltrados} 
                                    onAgregar={agregarAlCarrito} 
                                />
                            </Card.Body>
                        </Card>
                    </Col>

                    <Col xs={12} md={5}>
                        <Card className="shadow-sm border-0 bg-light">
                            <Card.Body>
                                <Card.Title>Resumen Venta Online</Card.Title>
                                <hr />
                                
                                {/* 🚀 CAMPO DE REFERENCIA EXTERNA (NUEVO) */}
                                <Form.Group className="mb-3">
                                    <Form.Label className="fw-bold">Referencia de Pedido Tienda Nube (#)</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={referenciaTiendaNube}
                                        onChange={(e) => setReferenciaTiendaNube(e.target.value)}
                                        placeholder="Ej: 102 (obligatorio para conciliar)"
                                    />
                                    <Form.Text className="text-muted">
                                        Este es el número de pedido original de Tienda Nube.
                                    </Form.Text>
                                </Form.Group>
                                
                                <hr />

                                <Carrito
                                    carrito={carrito}
                                    cambiarCantidad={cambiarCantidad} 
                                    quitarDelCarrito={quitarDelCarrito} 
                                    totalSinDescuento={totalSinDescuento} 
                                    gananciaBruta={0} 
                                />

                                <hr />
                                
                                <div className="d-flex justify-content-between align-items-center mt-3 pt-3 border-top border-dark border-3">
                                    <span className="fw-bold">TOTAL VENTA ONLINE</span>
                                    <h3 className="m-0 text-success fw-bold">
                                        ${totalFinal.toFixed(2)} 
                                    </h3>
                                </div>

                                <div className="mt-3 d-grid gap-2">
                                    <Button 
                                        variant="warning" 
                                        onClick={handleFinalizarTN} 
                                        disabled={loading || carrito.length === 0 || !vendedorTiendaNube || !referenciaTiendaNube.trim()}
                                    >
                                        {loading ? (
                                            <>
                                                <Spinner animation="border" size="sm" className="me-2" />
                                                Registrando...
                                            </>
                                        ) : (
                                            "Registrar y Descontar Stock"
                                        )}
                                    </Button>

                                    <Button variant="outline-secondary" onClick={limpiarCarrito}> 
                                        Limpiar carrito
                                    </Button>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
                
                
                {/* ----------------------------------------------------------------- */}
                {/* SECCIÓN: Listado de TODAS las Ventas Existentes */}
                {/* ----------------------------------------------------------------- */}
                <Row className="mt-5">
                    <Col>
                        <Card className="shadow-sm border-0">
                            <Card.Body>
                                
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <Card.Title className="text-primary m-0">Ventas de Tienda Nube Registradas</Card.Title>
                                    
                                    {/* SELECTOR DE ESTADO */}
                                    <div className="d-flex align-items-center">
                                        <span className="me-2 text-muted small">Filtrar por estado:</span>
                                        <Form.Select 
                                            style={{ width: '220px' }}
                                            value={filtroEstadoVenta}
                                            onChange={(e) => setFiltroEstadoVenta(e.target.value)}
                                            disabled={loadingVentas}
                                        >
                                            <option value="Todas">Todas (Total: {ventasTiendaNube.length})</option>
                                            <option value="PendienteCaja">Pendientes de Cobro</option>
                                            <option value="Cobrado">Cobradas/Ingresadas a Caja</option>
                                            <option value="Anulada">Anuladas</option>
                                        </Form.Select>
                                    </div>
                                    
                                </div>
                                
                                <Card.Subtitle className="text-muted mb-3">
                                    Ventas mostradas: **{ventasFiltradasPorEstado.length}**
                                </Card.Subtitle>
                                
                                {loadingVentas ? (
                                    <div className="text-center py-5">
                                        <Spinner animation="border" size="sm" className="me-2" /> Cargando ventas...
                                    </div>
                                ) : (
                                    <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
                                        {ventasFiltradasPorEstado.length === 0 ? (
                                            <Alert variant="secondary" className="text-center">
                                                No hay ventas de Tienda Nube registradas con el filtro "{filtroEstadoVenta}".
                                            </Alert>
                                        ) : (
                                            <ListGroup>
                                                {ventasFiltradasPorEstado.map((venta) => ( 
                                                    <ListGroup.Item 
                                                        key={venta.id} 
                                                        className="d-flex justify-content-between align-items-center"
                                                        action 
                                                        onClick={() => handleShowDetails(venta)}
                                                        style={{ cursor: 'pointer' }}
                                                        variant={venta.estado === 'anulada' ? 'secondary' : ''} 
                                                    >
                                                        <div className="flex-grow-1">
                                                            <div className="fw-bold text-dark">
                                                                Ticket ID: {venta.ticketId} 
                                                                {venta.estado === 'anulada' && <Badge bg="danger" className="ms-2">ANULADA</Badge>}
                                                                <Badge bg="info" className="ms-2">
                                                                    {venta.productos.length} items
                                                                </Badge>
                                                            </div>
                                                            {/* 🚀 MOSTRAR REFERENCIA EXTERNA */}
                                                            <div className="small text-primary fw-bold">
                                                                Ref Tienda Nube: #{venta.referenciaTiendaNube}
                                                            </div>
                                                            <div className="small text-muted">
                                                                Reg: {new Date(venta.fecha.seconds * 1000).toLocaleDateString('es-AR')}
                                                            </div>
                                                        </div>
                                                        
                                                        <div className="text-end">
                                                            <div className={`fw-bold ${venta.estadoPago === 'Cobrado' ? 'text-success' : 'text-warning'}`}>
                                                                {venta.estadoPago} / ${venta.totalFinal ? venta.totalFinal.toLocaleString('es-AR', { minimumFractionDigits: 2 }) : '0,00'}
                                                            </div>
                                                            <div className="small text-danger fw-semibold">
                                                                Cobro Est.: {venta.fechaCobroEstimada.toLocaleDateString('es-AR')}
                                                            </div>
                                                        </div>
                                                    </ListGroup.Item>
                                                ))}
                                            </ListGroup>
                                        )}
                                    </div>
                                )}
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </Container>
            
            {/* ----------------------------------------------------------------- */}
            {/* MODAL DE DETALLE DE VENTA */}
            {/* ----------------------------------------------------------------- */}
            <Modal show={showDetailModal} onHide={handleCloseDetails} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Detalle de Venta Online - ID: {selectedVenta?.ticketId}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedVenta ? (
                        <>
                            <Row className="mb-3">
                                <Col md={6}>
                                    <p><strong>Estado Venta:</strong> <Badge bg={selectedVenta.estado === 'anulada' ? 'danger' : 'success'}>{selectedVenta.estado.toUpperCase()}</Badge></p>
                                    <p><strong>Fecha Registro:</strong> {new Date(selectedVenta.fecha.seconds * 1000).toLocaleString('es-AR')}</p>
                                    <p><strong>Total Final:</strong> <span className="text-success fw-bold">${selectedVenta.totalFinal.toLocaleString('es-AR', { minimumFractionDigits: 2 })}</span></p>
                                </Col>
                                <Col md={6}>
                                    <p>
                                        <strong>Ref. Tienda Nube:</strong> 
                                        <Badge bg="primary" className="ms-2">#{selectedVenta.referenciaTiendaNube}</Badge>
                                    </p>
                                    <p><strong>Origen:</strong> {selectedVenta.origenVenta}</p>
                                    <p><strong>Estado Pago:</strong> <Badge bg={selectedVenta.estadoPago === 'Cobrado' ? 'success' : 'warning'}>{selectedVenta.estadoPago}</Badge></p>
                                    <p><strong>Cobro Estimado (7DH):</strong> <span className="text-danger fw-bold">{selectedVenta.fechaCobroEstimada.toLocaleDateString('es-AR')}</span></p>
                                </Col>
                            </Row>

                            {/* Botón para cambiar el estado de pago */}
                            <div className="d-grid gap-2 mb-4">
                                <Button 
                                    variant={selectedVenta.estadoPago === 'Cobrado' ? 'outline-success' : 'success'} 
                                    onClick={() => handleToggleCobradoStatus(selectedVenta)}
                                    disabled={loading || selectedVenta.estado === 'anulada'}
                                >
                                    {selectedVenta.estadoPago === 'Cobrado' ? 
                                        "Marcar como PENDIENTE DE COBRO" : 
                                        "Marcar como COBRADO / Ingresado a Caja"
                                    }
                                </Button>
                            </div>
                            
                            <h5>Productos</h5>
                            <Table striped bordered hover size="sm">
                                <thead>
                                    <tr>
                                        <th>Cód.</th>
                                        <th>Producto</th>
                                        <th>Cant.</th>
                                        <th className="text-end">P. Unitario</th>
                                        <th className="text-end">Subtotal</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {selectedVenta.productos.map((item, index) => (
                                        <tr key={index}>
                                            <td>{item.codigoProducto}</td>
                                            <td>{item.nombre}</td>
                                            <td>{item.cantidad}</td>
                                            <td className="text-end">${item.precioUnitario.toLocaleString('es-AR', { minimumFractionDigits: 2 })}</td>
                                            <td className="text-end">${item.subtotal.toLocaleString('es-AR', { minimumFractionDigits: 2 })}</td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot>
                                    <tr>
                                        <td colSpan="4" className="text-end fw-bold">TOTAL VENTA</td>
                                        <td className="text-end fw-bold">${selectedVenta.totalFinal.toLocaleString('es-AR', { minimumFractionDigits: 2 })}</td>
                                    </tr>
                                </tfoot>
                            </Table>
                        </>
                    ) : (
                        <p>Cargando detalle...</p>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    {/* Botón de Anular Venta */}
                    <Button 
                        variant="danger" 
                        onClick={() => handleAnularVenta(selectedVenta)} 
                        disabled={loading || selectedVenta?.estado === 'anulada'}
                    >
                        {loading && selectedVenta?.id === selectedVenta?.id ? <Spinner animation="border" size="sm" className="me-2" /> : ''}
                        {selectedVenta?.estado === 'anulada' ? 'Venta Anulada' : 'Anular Venta'}
                    </Button>

                    <Button variant="secondary" onClick={handleCloseDetails}>
                        Cerrar
                    </Button>
                </Modal.Footer>
            </Modal>


            <FooterDashboard/>
        </>
    );
};

export default CargaTiendaNube;